class ProductFeatures extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector(".slider");
    this.slides = this.querySelectorAll(".slider__slide");
    this.paginationDots = this.querySelectorAll(".slider-pagination-dot");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.isMobile = window.innerWidth <= 749;
    this.isScrolling = false;

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.init();
        this.setupResizeListener();
      });
    } else {
      this.init();
      this.setupResizeListener();
    }
  }

  init() {
    if (this.slider && this.isMobile) {
      this.setupMobileSlider();
    }
  }

  setupResizeListener() {
    window.addEventListener("resize", this.handleResize.bind(this), { passive: true });

    window.addEventListener("orientationchange", () => {
      setTimeout(() => {
        this.handleResize();
      }, 100);
    });
  }

  handleResize() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth <= 749;

    if (wasMobile !== this.isMobile) {
      // Reset loop initialization when switching between mobile/desktop
      if (this.slider && this.slider.dataset.loopInitialized) {
        // Remove clones if switching to desktop
        if (!this.isMobile) {
          const clones = this.slider.querySelectorAll(".is-clone");
          clones.forEach(clone => clone.remove());
          this.slider.removeAttribute("data-loop-initialized");
          this.slides = this.querySelectorAll(".slider__slide");
        }
      }
      this.init();
    } else if (this.isMobile && this.slider) {
      this.updateActiveSlide();
      this.updateArrowButtons();
    }
  }

  setupMobileSlider() {
    // Prevent double initialization
    if (this.slider.dataset.loopInitialized) return;

    // Only setup looping if we have more than one slide
    if (this.slides.length <= 1) {
      this.setupPaginationDots();
      this.updateActiveSlide();
      return;
    }

    // Clone first & last slide for infinite loop
    const firstSlide = this.slides[0].cloneNode(true);
    const lastSlide = this.slides[this.slides.length - 1].cloneNode(true);
    firstSlide.classList.add("is-clone");
    lastSlide.classList.add("is-clone");

    // Insert clones
    this.slider.insertBefore(lastSlide, this.slides[0]);
    this.slider.appendChild(firstSlide);

    // Re-select slides
    this.slides = this.querySelectorAll(".slider__slide");

    // Mark initialized
    this.slider.dataset.loopInitialized = "true";

    // Wait for layout to settle before scrolling
    requestAnimationFrame(() => {
      // Start at first real slide (index 1, after the first clone)
      const firstRealSlide = this.slides[1];
      if (firstRealSlide && this.slider) {
        const sliderWidth = this.slider.clientWidth;
        const slideWidth = firstRealSlide.offsetWidth;
        this.slider.scrollTo({
          left: firstRealSlide.offsetLeft - (sliderWidth / 2) + (slideWidth / 2),
          behavior: "auto"
        });
      }
    });

    // Events
    this.slider.addEventListener("touchstart", this.handleTouchStart.bind(this), { passive: true });
    this.slider.addEventListener("touchend", this.handleTouchEnd.bind(this), { passive: true });
    this.slider.addEventListener("scroll", this.handleScroll.bind(this), { passive: true });

    this.setupPaginationDots();
    this.setupArrowButtons();
    
    // Initial update after a short delay to ensure layout is ready
    setTimeout(() => {
      this.updateActiveSlide();
    }, 100);
  }

  setupArrowButtons() {
    // Check if arrow buttons setting is enabled
    const showArrowButtons = this.dataset.showArrowButtons === 'true';
    if (!showArrowButtons) {
      // Hide arrow buttons container if setting is disabled
      const buttonsContainer = this.querySelector(".slider-buttons");
      if (buttonsContainer) {
        buttonsContainer.style.display = 'none';
      }
      return;
    }

    if (!this.prevButton || !this.nextButton) return;

    // Remove existing listeners to prevent duplicates
    const newPrevButton = this.prevButton.cloneNode(true);
    const newNextButton = this.nextButton.cloneNode(true);
    this.prevButton.parentNode.replaceChild(newPrevButton, this.prevButton);
    this.nextButton.parentNode.replaceChild(newNextButton, this.nextButton);
    
    // Update references
    this.prevButton = newPrevButton;
    this.nextButton = newNextButton;

    // Add event listeners
    this.prevButton.addEventListener("click", (e) => {
      e.preventDefault();
      this.goToPreviousSlide();
    });

    this.nextButton.addEventListener("click", (e) => {
      e.preventDefault();
      this.goToNextSlide();
    });

    // Initial button state
    this.updateArrowButtons();
  }

  goToPreviousSlide() {
    if (!this.slider || !this.slides.length || this.isScrolling) return;

    const isLooping = this.slider.dataset.loopInitialized === "true";
    const sliderWidth = this.slider.clientWidth;
    const sliderRect = this.slider.getBoundingClientRect();
    const sliderCenter = sliderRect.left + sliderRect.width / 2;

    // Find current active slide
    let currentIndex = 0;
    let closestDistance = Infinity;

    this.slides.forEach((slide, index) => {
      const slideRect = slide.getBoundingClientRect();
      const slideCenter = slideRect.left + slideRect.width / 2;
      const distance = Math.abs(slideCenter - sliderCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        currentIndex = index;
      }
    });

    // Calculate previous slide index
    let prevIndex = currentIndex - 1;
    
    if (isLooping) {
      // If we're at the first real slide (index 1), go to last real slide
      if (prevIndex === 0) {
        prevIndex = this.slides.length - 2; // Last real slide
      }
    } else {
      // If not looping and at first slide, don't move
      if (prevIndex < 0) {
        return;
      }
    }

    const prevSlide = this.slides[prevIndex];
    if (!prevSlide) return;

    this.isScrolling = true;
    this.slider.scrollTo({
      left: prevSlide.offsetLeft - (sliderWidth / 2) + (prevSlide.offsetWidth / 2),
      behavior: "smooth"
    });

    setTimeout(() => {
      this.isScrolling = false;
      this.updateActiveSlide();
      this.updateArrowButtons();
    }, 300);
  }

  goToNextSlide() {
    if (!this.slider || !this.slides.length || this.isScrolling) return;

    const isLooping = this.slider.dataset.loopInitialized === "true";
    const sliderWidth = this.slider.clientWidth;
    const sliderRect = this.slider.getBoundingClientRect();
    const sliderCenter = sliderRect.left + sliderRect.width / 2;

    // Find current active slide
    let currentIndex = 0;
    let closestDistance = Infinity;

    this.slides.forEach((slide, index) => {
      const slideRect = slide.getBoundingClientRect();
      const slideCenter = slideRect.left + slideRect.width / 2;
      const distance = Math.abs(slideCenter - sliderCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        currentIndex = index;
      }
    });

    // Calculate next slide index
    let nextIndex = currentIndex + 1;
    
    if (isLooping) {
      // If we're at the last real slide, go to first real slide
      if (nextIndex >= this.slides.length - 1) {
        nextIndex = 1; // First real slide (skip clone at index 0)
      }
    } else {
      // If not looping and at last slide, don't move
      if (nextIndex >= this.slides.length) {
        return;
      }
    }

    const nextSlide = this.slides[nextIndex];
    if (!nextSlide) return;

    this.isScrolling = true;
    this.slider.scrollTo({
      left: nextSlide.offsetLeft - (sliderWidth / 2) + (nextSlide.offsetWidth / 2),
      behavior: "smooth"
    });

    setTimeout(() => {
      this.isScrolling = false;
      this.updateActiveSlide();
      this.updateArrowButtons();
    }, 300);
  }

  updateArrowButtons() {
    if (!this.prevButton || !this.nextButton) return;

    const isLooping = this.slider.dataset.loopInitialized === "true";
    
    // With looping enabled, buttons are always enabled
    if (isLooping) {
      this.prevButton.removeAttribute("disabled");
      this.nextButton.removeAttribute("disabled");
      return;
    }

    // Without looping, disable buttons at ends
    if (this.slides.length <= 1) {
      this.prevButton.setAttribute("disabled", "disabled");
      this.nextButton.setAttribute("disabled", "disabled");
      return;
    }

    const sliderRect = this.slider.getBoundingClientRect();
    const sliderCenter = sliderRect.left + sliderRect.width / 2;
    const scrollLeft = this.slider.scrollLeft;
    const sliderWidth = this.slider.clientWidth;
    const maxScroll = this.slider.scrollWidth - sliderWidth;

    // Check if at start
    const firstSlide = this.slides[0];
    const firstSlideLeft = firstSlide ? firstSlide.offsetLeft : 0;
    const isAtStart = scrollLeft <= firstSlideLeft + 10;

    // Check if at end
    const lastSlide = this.slides[this.slides.length - 1];
    const lastSlideRight = lastSlide ? lastSlide.offsetLeft + lastSlide.offsetWidth : 0;
    const isAtEnd = scrollLeft >= maxScroll - 10 || scrollLeft >= lastSlideRight - sliderWidth - 10;

    if (isAtStart) {
      this.prevButton.setAttribute("disabled", "disabled");
    } else {
      this.prevButton.removeAttribute("disabled");
    }

    if (isAtEnd) {
      this.nextButton.setAttribute("disabled", "disabled");
    } else {
      this.nextButton.removeAttribute("disabled");
    }
  }

  setupPaginationDots() {
    // Check if pagination dots setting is enabled
    const showPaginationDots = this.dataset.showPaginationDots === 'true';
    if (!showPaginationDots) {
      // Hide pagination container if setting is disabled
      const paginationContainer = this.querySelector(".slider-pagination");
      if (paginationContainer) {
        paginationContainer.style.display = 'none';
      }
      return;
    }

    // Create pagination dots if they don't exist
    if (!this.paginationDots.length && this.slides.length > 1) {
      this.createPaginationDots();
    }

    if (!this.paginationDots.length) return;

    this.paginationDots.forEach((dot, index) => {
      // Remove existing listeners to prevent duplicates
      const newDot = dot.cloneNode(true);
      dot.parentNode.replaceChild(newDot, dot);
      
      newDot.addEventListener("click", () => {
        this.goToSlide(index);
      });
    });
    
    // Re-select dots after cloning
    this.paginationDots = this.querySelectorAll(".slider-pagination-dot");
  }

  createPaginationDots() {
    // Count real slides (excluding clones)
    const realSlides = Array.from(this.slides).filter(slide => !slide.classList.contains("is-clone"));
    if (realSlides.length <= 1) return;

    // Find or create pagination container
    let paginationContainer = this.querySelector(".slider-pagination");
    if (!paginationContainer) {
      paginationContainer = document.createElement("div");
      paginationContainer.className = "slider-pagination";
      this.slider.parentElement.appendChild(paginationContainer);
    }

    let dotsContainer = paginationContainer.querySelector(".slider-pagination-dots");
    if (!dotsContainer) {
      dotsContainer = document.createElement("div");
      dotsContainer.className = "slider-pagination-dots";
      paginationContainer.appendChild(dotsContainer);
    }

    // Clear existing dots
    dotsContainer.innerHTML = "";

    // Create dots for each real slide
    realSlides.forEach((slide, index) => {
      const dot = document.createElement("button");
      dot.type = "button";
      dot.className = `slider-pagination-dot${index === 0 ? " is-active" : ""}`;
      dot.setAttribute("aria-label", `Go to slide ${index + 1}`);
      dot.setAttribute("aria-current", index === 0 ? "true" : "false");
      
      const span = document.createElement("span");
      span.className = "visually-hidden";
      span.textContent = `Go to slide ${index + 1}`;
      dot.appendChild(span);
      
      dotsContainer.appendChild(dot);
    });

    // Re-select dots
    this.paginationDots = this.querySelectorAll(".slider-pagination-dot");
  }

  goToSlide(slideIndex) {
    const total = this.paginationDots.length;
    if (!this.slider || total === 0) return;

    // Wrap index for looping
    if (slideIndex < 0) slideIndex = total - 1;
    if (slideIndex >= total) slideIndex = 0;

    // Determine target slide based on whether looping is enabled
    const isLooping = this.slider.dataset.loopInitialized === "true";
    let targetSlide;
    
    if (isLooping) {
      // +1 to skip first clone
      targetSlide = this.slides[slideIndex + 1];
    } else {
      // No clones, use index directly
      targetSlide = this.slides[slideIndex];
    }

    if (!targetSlide) return;

    this.updatePaginationDots(slideIndex);

    // Scroll horizontally only, prevent page scroll
    this.slider.scrollTo({
      left: targetSlide.offsetLeft - (this.slider.clientWidth / 2) + (targetSlide.offsetWidth / 2),
      behavior: "smooth"
    });

    setTimeout(() => {
      this.updateActiveSlide();
      this.updateArrowButtons();
    }, 300);
  }

  updatePaginationDots(activeIndex) {
    this.paginationDots.forEach((dot, index) => {
      if (index === activeIndex) {
        dot.classList.add("is-active");
      } else {
        dot.classList.remove("is-active");
      }
    });
  }

  handleTouchStart(e) {
    this.touchStartX = e.touches[0].clientX;
    this.touchStartY = e.touches[0].clientY;
  }

  handleTouchEnd(e) {
    if (!this.touchStartX || !this.touchStartY) return;

    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const diffX = this.touchStartX - touchEndX;
    const diffY = this.touchStartY - touchEndY;

    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
      setTimeout(() => {
        this.ensureCenteredSlide();
        this.updateActiveSlide();
        this.updateArrowButtons();
      }, 300);
    }

    this.touchStartX = null;
    this.touchStartY = null;
  }

  handleScroll() {
    if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
    if (this.isScrolling) return; // Prevent multiple simultaneous scrolls

    this.scrollTimeout = setTimeout(() => {
      if (!this.slider || !this.slides.length) return;

      const sliderWidth = this.slider.clientWidth;
      const scrollLeft = this.slider.scrollLeft;
      const sliderRect = this.slider.getBoundingClientRect();
      const sliderCenter = sliderRect.left + sliderRect.width / 2;

      const firstReal = this.slides[1];
      const lastReal = this.slides[this.slides.length - 2];
      const firstClone = this.slides[0];
      const lastClone = this.slides[this.slides.length - 1];

      if (!firstReal || !lastReal || !firstClone || !lastClone) {
        this.updateActiveSlide();
        this.updateArrowButtons();
        return;
      }

      // Check scroll position relative to clones
      const firstCloneLeft = firstClone.offsetLeft;
      const lastCloneLeft = lastClone.offsetLeft;
      const firstRealLeft = firstReal.offsetLeft;
      const lastRealLeft = lastReal.offsetLeft;

      // If we've scrolled past the first clone (scrolled to the left), jump to last real
      if (scrollLeft < firstRealLeft - sliderWidth / 2) {
        this.isScrolling = true;
        this.slider.scrollTo({
          left: lastRealLeft - (sliderWidth / 2) + (lastReal.offsetWidth / 2),
          behavior: "auto"
        });
        setTimeout(() => {
          this.isScrolling = false;
          this.updateActiveSlide();
          this.updateArrowButtons();
        }, 50);
        return;
      }

      // If we've scrolled past the last clone (scrolled to the right), jump to first real
      const maxScroll = this.slider.scrollWidth - sliderWidth;
      if (scrollLeft > lastRealLeft + lastReal.offsetWidth - sliderWidth / 2) {
        this.isScrolling = true;
        this.slider.scrollTo({
          left: firstRealLeft - (sliderWidth / 2) + (firstReal.offsetWidth / 2),
          behavior: "auto"
        });
        setTimeout(() => {
          this.isScrolling = false;
          this.updateActiveSlide();
          this.updateArrowButtons();
        }, 50);
        return;
      }

      this.updateActiveSlide();
      this.updateArrowButtons();
    }, 100);
  }

  updateActiveSlide() {
    if (!this.slider || !this.slides.length) return;

    const sliderRect = this.slider.getBoundingClientRect();
    const sliderCenter = sliderRect.left + sliderRect.width / 2;

    let activeSlide = null;
    let activeIndex = 0;
    let closestDistance = Infinity;

    this.slides.forEach((slide, index) => {
      slide.classList.remove("is-active");

      const slideRect = slide.getBoundingClientRect();
      const slideCenter = slideRect.left + slideRect.width / 2;
      const distance = Math.abs(slideCenter - sliderCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        activeSlide = slide;
        activeIndex = index;
      }
    });

    if (activeSlide) {
      activeSlide.classList.add("is-active");

      // Convert to real index (exclude clones)
      // If looping is enabled, we have clones at index 0 and last index
      const isLooping = this.slider.dataset.loopInitialized === "true";
      let realIndex = activeIndex;
      
      if (isLooping) {
        // Skip the first clone (index 0)
        realIndex = activeIndex - 1;
        // Handle wrap-around for clones
        if (realIndex < 0) {
          // We're on the first clone, show last real slide
          realIndex = this.paginationDots.length - 1;
        } else if (realIndex >= this.paginationDots.length) {
          // We're on the last clone, show first real slide
          realIndex = 0;
        }
      }

      if (this.paginationDots.length > 0) {
        this.updatePaginationDots(realIndex);
      }
      
      // Update arrow buttons
      this.updateArrowButtons();
    }
  }

  ensureCenteredSlide() {
    if (!this.slider) return;

    const sliderRect = this.slider.getBoundingClientRect();
    const sliderCenter = sliderRect.left + sliderRect.width / 2;

    let closestSlide = null;
    let closestDistance = Infinity;

    this.slides.forEach((slide) => {
      const slideRect = slide.getBoundingClientRect();
      const slideCenter = slideRect.left + slideRect.width / 2;
      const distance = Math.abs(slideCenter - sliderCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestSlide = slide;
      }
    });

    if (closestSlide && this.slider) {
      // Scroll horizontally only, prevent page scroll
      this.slider.scrollTo({
        left: closestSlide.offsetLeft - (this.slider.clientWidth / 2) + (closestSlide.offsetWidth / 2),
        behavior: "smooth"
      });
    }
  }
}

// Register element
if (!customElements.get("product-features")) {
  customElements.define("product-features", ProductFeatures);
}
